import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuTitleWithLabel } from 'ui-components/components/dropdown-menu-title-with-label';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { testId } from 'ui-components/services/testid-service';

import { PackageGroup } from './package-group';
import { emptyArray } from 'ui-components/default-props/empty';
import { MessageDescriptorPropType } from '../../prop-types/intl';

const messages = defineMessages({
  selectPackage: {
    description: 'packageDropdown.selectPackage',
    defaultMessage: 'Select Package',
    id: 'L0wycH',
  },
  package: {
    description: 'packageDropdown.package',
    defaultMessage: 'Package',
    id: 'wFOTml',
  },
  packages: {
    description: 'packageSelector.packages',
    defaultMessage: 'Packages',
    id: 'Q7iVmW',
  },
  customPackages: {
    description: 'packageDropdown.customPackages',
    defaultMessage: 'My Custom Packages',
    id: 'C1QxfT',
  },
});

const renderToggler = (simpleToggler, togglerClassName, selectedPackageName, hasError, intl, defaultEmptyText, theme) => {
  const title = selectedPackageName || defaultEmptyText;
  if (simpleToggler) {
    return (
      <DropdownMenuToggler className={togglerClassName} {...testId('package-dropdown-toggler')} theme={theme}>
        {title}
      </DropdownMenuToggler>
    );
  }
  return (
    <DropdownMenuToggler className={togglerClassName} hasError={hasError} {...testId('package-dropdown-toggler')} theme={theme}>
      <DropdownMenuTitleWithLabel label={intl.formatMessage(messages.package)} title={title} />
    </DropdownMenuToggler>
  );
};

const defaultDefaultEmptyText = <FormattedMessage {...messages.selectPackage} />;

export const PackageDropdown = ({
  className,
  disabled = false,
  onChange,
  togglerClassName,
  selectedPackageKey,
  selectedPackageName,
  defaultEmptyText = defaultDefaultEmptyText,
  groupedPackages = emptyArray,
  customPackages = emptyArray,
  dropdownMenuShouldHaveWidthOfButton = false,
  hasError = false,
  simpleToggler = false,
  variant = 'button',
  itemClassName = '',
  groupClassName = '',
  fullWidth = false,
  theme = THEME_CONSTANTS.tertiary,
  showEmptyOption = false,
}) => {
  const intl = useIntl();

  const handleEmptySelection = useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <DropdownMenu
      className={className}
      disabled={disabled}
      dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
      fullWidth={fullWidth}
      variant={variant}
    >
      {renderToggler(simpleToggler, togglerClassName, selectedPackageName, hasError, intl, defaultEmptyText, theme)}
      {showEmptyOption && (
        <DropdownMenuItem
          key=''
          onClick={handleEmptySelection}
          selected={selectedPackageKey === undefined && selectedPackageName === undefined}
        >
          {defaultEmptyText}
        </DropdownMenuItem>
      )}
      {customPackages?.length ? (
        <PackageGroup
          groupClassName={groupClassName}
          groupName={<FormattedMessage {...messages.customPackages} />}
          itemClassName={itemClassName}
          onChange={onChange}
          packages={customPackages}
          selectedPackageKey={selectedPackageKey}
        />
      ) : null}
      {groupedPackages.map(({ groupName, packages }, index) => (
        <PackageGroup
          key={`${groupName}-${index}`}
          groupClassName={groupClassName}
          groupName={<FormattedMessage {...groupName} />}
          itemClassName={itemClassName}
          onChange={onChange}
          packages={packages}
          selectedPackageKey={selectedPackageKey}
        />
      ))}
    </DropdownMenu>
  );
};

PackageDropdown.propTypes = {
  groupedPackages: PropTypes.arrayOf(
    PropTypes.shape({
      groupName: MessageDescriptorPropType,
      packages: PropTypes.arrayOf(
        PropTypes.shape({
          packageTypeId: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
    }),
  ),
  customPackages: PropTypes.arrayOf(
    PropTypes.shape({
      packageTypeId: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  /**
   * Called when a package is selected. When used with the container, this will receive
   * the package object from PackageService.findById(). If the package is not found
   * (e.g., when selecting the empty option via showEmptyOption), this will receive undefined.
   * Consumers should handle undefined values appropriately.
   */
  onChange: PropTypes.func.isRequired,
  togglerClassName: PropTypes.string,
  selectedPackageKey: PropTypes.string,
  selectedPackageName: PropTypes.string,
  defaultEmptyText: PropTypes.node,
  dropdownMenuShouldHaveWidthOfButton: PropTypes.bool,
  hasError: PropTypes.bool,
  simpleToggler: PropTypes.bool,
  variant: PropTypes.string,
  itemClassName: PropTypes.string,
  groupClassName: PropTypes.string,
  /**
   * When true, renders a "Same as Outbound" (or defaultEmptyText) menu item as the first option.
   * Selecting this option calls onChange(''), which typically results in the onChange handler
   * receiving undefined from PackageService.findById(). Consumers must handle undefined values.
   */
  showEmptyOption: PropTypes.bool,
};
